import * as Yup from "yup";
import { currentAuthSession } from "../components/user/BCPAuthProvider";
import { getEnvironmentVariable } from "./environmentVariables";
const AMPLIFY_BRANCH = getEnvironmentVariable("AMPLIFY_BRANCH");
const IS_DEV = getEnvironmentVariable("IS_DEV") === "true";
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const STRIPE_PRICING = getEnvironmentVariable("STRIPE_PRICING")
  ? JSON.parse(getEnvironmentVariable("STRIPE_PRICING"))
  : undefined;

const getApprovalReasons = () => {
  if (
    AMPLIFY_BRANCH === "testing" ||
    AMPLIFY_BRANCH === "testing-organizations-overhaul"
  ) {
    return [
      {
        label: "Unable to verify business address",
        text: "We could not verify the address you provided belongs to the business you are organizing events with. If you are organizing events with a brick and mortar store, please reply to this email with the current address of that store and a current picture of the exterior of the business. If you are organizing events with an online-only store, please resubmit your application with the address of an off-site venue where the events will be hosted. Please note that events cannot be hosted at your personal residence."
      },
      {
        label: "Unable to verify business license",
        text: "We are unable to verify your business license. The picture you provided may have been too blurry or too small to make our decision. Please reply to this email with a new picture of your business license."
      },
      {
        label: "Unable to determine adequate play space",
        text: "We are unable to determine if you have adequate play space for at least eight (8) players. You must be able to safely and comfortably seat at least eight (8) players. The picture you provided may have been too blurry or too small to make our decision. Please reply to this email with the requested pictures of tables, chairs, and the surrounding area."
      },
      {
        label: "Unable to verify retail area",
        text: "We are unable to verify your retail area. The picture you provided may have been too blurry or too small to make our decision. Please reply to this email with a new picture of the retail area."
      },
      {
        label: "Unable to verify exterior of business",
        text: "We are unable to verify the exterior of your business. The picture you provided may have been too blurry or too small to make our decision. Please reply to this email with a new picture of the exterior of your business."
      },
      { label: "Non-business address (residence, P.O. Box, etc.)" },
      { label: "Unable to Verify Business" },
      { label: "Banned" }
    ];
  }
  return;
};

const getSubscriptions = () => {
  if (NO_SUBSCRIPTIONS) return {};

  if (STRIPE_PRICING?.subscriptions) {
    return STRIPE_PRICING.subscriptions;
  }

  if (IS_DEV) {
    return {
      playerSub: "prod_LCzrZUmAkdRSPI",
      circuitSuite: "prod_LCzrUstZ8vlp6b"
    };
  }

  return {
    playerSub: "prod_LSJA3LHr2MFiVa",
    circuitSuite: "prod_LSJAYaOUBleUdY"
  };
};

const getProducts = () => {
  if (NO_SUBSCRIPTIONS) return {};

  if (STRIPE_PRICING?.products) {
    return STRIPE_PRICING.products;
  }

  if (IS_DEV) {
    return {
      prod_LCzrZUmAkdRSPI: {
        type: "Subscription",
        name: "BCP Subscription",
        coupons: {
          SAVER20: { discount: { percentage: 0.2, onceOff: true } },
          MYWARHAMMERLINK: { discount: { percentage: 0.2, onceOff: true } }
        },
        prices: [
          {
            priceId: "price_1KWZrKKSvDrUcAmj8SNM2xZx",
            frequency: "Monthly",
            amount: 4.99
          },
          {
            priceId: "price_1KWZrKKSvDrUcAmjgzOO4MJm",
            frequency: "Bi-Annual",
            amount: 27.99
          },
          {
            priceId: "price_1KWZrKKSvDrUcAmj8z10HSfP",
            frequency: "Yearly",
            amount: 49.99
          }
        ]
      },
      prod_LCzrUstZ8vlp6b: {
        type: "StoreSubscription",
        name: "Circuit Suite Subscription",
        prices: [
          {
            priceId: "price_1KWZruKSvDrUcAmjLbTGj3x8",
            frequency: "Monthly",
            amount: 50
          },
          {
            priceId: "price_1KWZruKSvDrUcAmj9apcpLa5",
            frequency: "Yearly",
            amount: 500
          }
        ]
      }
    };
  }

  return {
    prod_LSJA3LHr2MFiVa: {
      type: "Subscription",
      name: "BCP Subscription",
      coupons: {
        TABLETOPTITANS: {
          code: "TABLETOPTITANS",
          discount: { percentage: 0.2, onceOff: true },
          frequencies: { Yearly: true }
        },
        TITANS: {
          code: "TITANS",
          discount: { percentage: 0.2, onceOff: true },
          frequencies: { Yearly: true }
        },
        SAVER20: {
          code: "SAVER20",
          discount: { percentage: 0.2, onceOff: true },
          frequencies: { Yearly: true }
        }
      },
      prices: [
        {
          priceId: "price_1KlOYVKSvDrUcAmjUamMvpHV",
          frequency: "Monthly",
          amount: 4.99
        },
        {
          priceId: "price_1KlOYVKSvDrUcAmjkzrJy2TO",
          frequency: "Bi-Annual",
          amount: 27.99
        },
        {
          priceId: "price_1KlOYVKSvDrUcAmjVhaZQcLZ",
          frequency: "Yearly",
          amount: 49.99
        }
      ]
    },
    prod_LSJAYaOUBleUdY: {
      type: "StoreSubscription",
      name: "Circuit Suite Subscription",
      coupons: {
        AUSCS50: {
          code: "AUSCS50",
          discount: { percentage: 0.35, onceOff: true },
          frequencies: { Yearly: true }
        }
      },
      prices: [
        {
          priceId: "price_1KlOYdKSvDrUcAmj8Ab1DJ6P",
          frequency: "Monthly",
          amount: 50
        },
        {
          priceId: "price_1KlOYdKSvDrUcAmjbB9cRWBb",
          frequency: "Yearly",
          amount: 500
        }
      ]
    }
  };
};

export function getTicketing() {
  if (COMPANY_FLAG === "solforge") {
    return {
      USD: { Percent: 7, Fixed: 0.3 },
      GBP: { Percent: 6.6, Fixed: 0.2 },
      EUR: { Percent: 6.6, Fixed: 0.2 },
      CAD: { Percent: 7, Fixed: 0.3 },
      AUD: { Percent: 7, Fixed: 0.3 },
      NZD: { Percent: 7, Fixed: 0.3 },
      MXN: { Percent: 7.7, Fixed: 3 },
      JPY: { Percent: 7.7, Fixed: 0 },
      BRL: { Percent: 8, Fixed: 0.5 },
      SGD: { Percent: 7.5, Fixed: 0.5 },
      SEK: { Percent: 7, Fixed: 2 },
      DKK: { Percent: 7, Fixed: 2 },
      NOK: { Percent: 7, Fixed: 2 },
      HUF: { Percent: 6.6, Fixed: 85 }
    };
  }
  return {
    USD: { Percent: 5, Fixed: 0.3, PercentPlayer: 5, FixedPlayer: 1 },
    GBP: {
      Percent: 4.7,
      Fixed: 0.2,
      PercentPlayer: 4.5,
      FixedPlayer: 1
    },
    EUR: { Percent: 4.7, Fixed: 0.2, PercentPlayer: 5, FixedPlayer: 1 },
    CAD: { Percent: 5, Fixed: 0.3, PercentPlayer: 5, FixedPlayer: 1 },
    AUD: { Percent: 5, Fixed: 0.3, PercentPlayer: 4.5, FixedPlayer: 1 },
    NZD: { Percent: 5, Fixed: 0.3, PercentPlayer: 5, FixedPlayer: 1 },
    MXN: {
      Percent: 5.7,
      Fixed: 3,
      PercentPlayer: 4.5,
      FixedPlayer: 20.73
    },
    BRL: { Percent: 6, Fixed: 0.5, PercentPlayer: 4.5, FixedPlayer: 6 },
    SGD: {
      Percent: 5.5,
      Fixed: 0.5,
      PercentPlayer: 4.5,
      FixedPlayer: 1.85
    },
    SEK: { Percent: 5, Fixed: 2, PercentPlayer: 3, FixedPlayer: 10.6 },
    DKK: { Percent: 5, Fixed: 2, PercentPlayer: 3, FixedPlayer: 10.6 },
    NOK: { Percent: 5, Fixed: 2, PercentPlayer: 3, FixedPlayer: 10.6 },
    HUF: { Percent: 4.7, Fixed: 85, PercentPlayer: 4, FixedPlayer: 370 },
    CHF: { Percent: 5, Fixed: 1, PercentPlayer: 5, FixedPlayer: 1 }
  };
}

const getPricing = () => {
  return {
    tickets: getTicketing(),
    subscriptions: getSubscriptions(),
    products: getProducts()
  };
};

const getOnboarding = () => {
  if (
    AMPLIFY_BRANCH === "testing" ||
    AMPLIFY_BRANCH === "testing-organizations-overhaul"
  ) {
    return {
      gameStore: {
        phoneNumber: {
          label: "Store Phone Number",
          type: "phone",
          required: true
        },
        storeEmail: {
          label: "Store Email",
          type: "email",
          required: true
        },
        carryProduct: {
          label: "Does the store currently carry our product?",
          type: "radio",
          required: true,
          options: ["Yes", "No"]
        },
        gallery: {
          label:
            "Please upload pictures of your store. You must include the following: Play space, Register/Point of Sale, Outside entrance of your store. Additional photos will help verify your store is eligible for Organized Play",
          type: "gallery",
          required: true,
          max: 10
        }
      },
      organizer: {
        position: {
          label: "Relation to the Store",
          error: "Store Pictures",
          type: "text",
          required: true
        },
        attachment: {
          label: "Please provide endorsement from your LGS",
          type: "attachments"
        },
        policy: {
          label:
            "I acknowledge that as an Organization or Store Owner it is my responsibility to uphold the expectations from both the Tournament Policy and the LGS TOS.",
          type: "checkbox",
          required: true
        }
      }
    };
  }
  if (AMPLIFY_BRANCH === "catan") {
    return {
      gameStore: {
        phoneNumber: {
          label: "Store Phone Number",
          type: "phone",
          required: true
        },
        storeEmail: {
          label: "Store Email",
          type: "email",
          required: true
        },
        gallery: {
          label:
            "Please upload pictures of your store. You must include the following: Play space, Register/Point of Sale, Outside entrance of your store. Additional photos will help verify your store is eligible for Organized Play",
          type: "gallery",
          required: true,
          max: 10
        }
      }
    };
  }
  if (COMPANY_FLAG === "solforge") {
    return {
      gameStore: {
        phoneNumber: {
          label: "Store Phone Number",
          type: "text",
          required: true
        },
        storeEmail: {
          label: "Store Email",
          type: "email",
          required: true
        },
        carryProduct: {
          label: "Does the store currently carry Solforge Fusion product?",
          type: "radio",
          required: true,
          options: ["Yes", "No"]
        },
        gallery: {
          label:
            "Please upload pictures of your store. You must include the following: Play space, Register/Point of Sale, Outside entrance of your store. Additional photos will help verify your store is eligible for Organized Play",
          type: "gallery",
          required: true,
          max: 10
        }
      },
      organizer: {
        position: {
          label: "Relation to the Store",
          type: "text",
          required: true
        },
        attachment: {
          label: "Please provide endorsement from your LGS",
          type: "attachments"
        },
        policy: {
          label:
            "I acknowledge that as an Organization or Store Owner it is my responsibility to uphold the expectations from both the Solforge Fusion Tournament Policy.",
          type: "checkbox",
          required: true
        }
      }
    };
  }

  return {};
};

const getUserSource = () => {
  return {
    label: "How did you hear about us?",
    required: true,
    list: [
      { label: "Facebook" },
      { label: "Discord" },
      { label: "Twitter" },
      { label: "Friend" },
      { label: "Local Game Store", secondaryLabel: "Store Name" },
      { label: "Convention/Event", secondaryLabel: "Event Name" },
      { label: "Content Creator", secondaryLabel: "Creator Name" },
      { label: "WebSearch (Google)" },
      {
        label: "Referral Code",
        secondaryLabel: "Referrer/Referral Code"
      },
      {
        label: "Other",
        secondaryLabel: "Other"
      }
    ]
  };
};

const getAPIEndpoints = () => {
  let apiEndpoints = [
    {
      name: "API",
      endpoint: `${getEnvironmentVariable("API")}/v1`,
      region: getEnvironmentVariable("AUTH_REGION"),
      custom_header: async () => {
        var header = {
          "client-id": getEnvironmentVariable("AUTH_CLIENT_ID")
        };
        try {
          var currentSession = await currentAuthSession();
          if (currentSession.accessTokenString) {
            header.Authorization = `Bearer ${currentSession.accessTokenString}`;
          }
        } catch (err) {
          console.error(err);
        }
        return header;
      }
    },
    {
      name: "v2",
      endpoint: `${getEnvironmentVariable("API")}/v2`,
      region: getEnvironmentVariable("AUTH_REGION"),
      custom_header: async () => {
        var header = {
          "client-id": getEnvironmentVariable("AUTH_CLIENT_ID")
        };
        try {
          var currentSession = await currentAuthSession();
          if (currentSession.accessTokenString) {
            header.Authorization = `Bearer ${currentSession.accessTokenString}`;
          }
        } catch (err) {
          console.error(err);
        }
        return header;
      }
    },
    {
      name: "APIUnsecured",
      endpoint: `${getEnvironmentVariable("API")}/v1`,
      region: getEnvironmentVariable("AUTH_REGION"),
      custom_header: async () => {
        var header = {
          "client-id": getEnvironmentVariable("AUTH_CLIENT_ID")
        };
        return header;
      }
    }
  ];

  if (COMPANY_FLAG === "solforge") {
    if (IS_DEV) {
      apiEndpoints.push(
        {
          name: "SolforgeBearer",
          endpoint:
            "https://05jflsrfm0.execute-api.us-east-1.amazonaws.com/bcpdev",
          region: "us-east-1",
          custom_header: async () => {
            var header = {};

            try {
              var currentSession = await currentAuthSession();
              if (currentSession.accessTokenString) {
                header.Authorization = `Bearer ${currentSession.accessTokenString}`;
              }
            } catch (err) {
              header = {};
            }
            return header;
          }
        },
        {
          name: "Solforge",
          endpoint:
            "https://05jflsrfm0.execute-api.us-east-1.amazonaws.com/bcpdev",
          region: "us-east-1"
        }
      );
    } else {
      apiEndpoints.push(
        {
          name: "SolforgeBearer",
          endpoint:
            "https://ul51g2rg42.execute-api.us-east-1.amazonaws.com/main",
          region: "us-east-1",
          custom_header: async () => {
            var header = {};
            try {
              var currentSession = await currentAuthSession();
              if (currentSession.accessTokenString) {
                header.Authorization = `Bearer ${currentSession.accessTokenString}`;
              }
            } catch (err) {
              header = {};
            }
            return header;
          }
        },
        {
          name: "Solforge",
          endpoint:
            "https://ul51g2rg42.execute-api.us-east-1.amazonaws.com/main",
          region: "us-east-1"
        }
      );
    }
  }

  return apiEndpoints;
};

const getRedirects = () => {
  if (COMPANY_FLAG === "solforge") {
    return {
      redirect: {
        signup: `${getEnvironmentVariable(
          "HEADER_SOLFORGE_URL"
        )}/login?step=signup`,
        forgot: `${getEnvironmentVariable(
          "HEADER_SOLFORGE_URL"
        )}/login?step=forgot`,
        verify: `${getEnvironmentVariable(
          "HEADER_SOLFORGE_URL"
        )}/login?step=verify`
      }
    };
  }

  return {};
};

const getAdditionalLinks = () => {
  if (COMPANY_FLAG === "solforge") {
    return {
      links: {
        profileMenu: [
          {
            label: "Account Settings",
            url: "https://solforgefusion.com/app/account"
          },
          {
            label: "OP Settings",
            url: "/account"
          }
        ]
      }
    };
  }

  return {};
};

const getEventSubTypes = () => {
  if (COMPANY_FLAG === "solforge") {
    return {
      eventSubTypes: {
        label: "Event Tier",
        list: ["Bronze", "Silver", "Gold", "Platinum", "Casual"]
      }
    };
  }
  return {};
};

export function globalVariables() {
  return {
    site: {
      title: getEnvironmentVariable("SITE_NAME"),
      url: window.origin,
      description: getEnvironmentVariable("SITE_DESCRIPTION"),
      logo: `${SYSTEM_IMAGES_CLOUDFRONT}/images/favicon.png`,
      supportUrl:
        getEnvironmentVariable("COMPANY_SUPPORT_URL") ||
        "https://bestcoastpairings.zendesk.com"
    },
    auth: {
      login: {
        username: {
          cognito: "username",
          yup: Yup.string().max(128).required("Required"),
          label: COMPANY_FLAG === "solforge" ? "Username or Email" : "Email"
        }
      },
      ...getRedirects()
    },
    amplify: {
      userPoolId: getEnvironmentVariable("AUTH_POOL_ID"),
      region: getEnvironmentVariable("AUTH_REGION"),
      userPoolWebClientId: getEnvironmentVariable("AUTH_CLIENT_ID"),
      authenticationFlowType: "USER_PASSWORD_AUTH",
      API: {
        endpoints: getAPIEndpoints()
      }
    },

    userSource: getUserSource(),
    onboarding: getOnboarding(),
    pricing: getPricing(),
    approvalReasons: getApprovalReasons(),
    ...getAdditionalLinks(),
    ...getEventSubTypes()
  };
}
